import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter, useRoute } from 'vue-router';

export default function useInactivityMonitor() {
  const authStore = useAuthStore();
  const router = useRouter();
  const route = useRoute();

  const inactivityTimer = ref(null);
  const warningTimer = ref(null);
  const isWarningVisible = ref(false);
  const remainingSeconds = ref(30);
  const countdownInterval = ref(null);
  const isLoggingOut = ref(false);
  const isMonitoringPaused = ref(false);

  // List of routes where inactivity monitoring should be disabled
  const paginasComInatividadePermitida = ['/gerenciar-pedidos'];

  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos
  const WARNING_DURATION = 30 * 1000; // 30 segundos

  const events = [
    'mousedown', 'mousemove', 'keypress',
    'scroll', 'touchstart', 'click', 'keydown'
  ];

  // Check if current route should be excluded from monitoring
  const shouldExcludeRoute = () => {
    return paginasComInatividadePermitida.some(path => route.path === path || route.path.startsWith(path + '/'));
  };

  // Resetar o temporizador de inatividade
  const resetInactivityTimer = () => {
    // Não resetamos se o aviso estiver visível ou se estivermos em uma rota excluída
    if (!isWarningVisible.value && !isMonitoringPaused.value) {
      clearTimers();
      startTimers();
    }
  };

  // Iniciar os temporizadores
  const startTimers = () => {
    // Não iniciar temporizadores se estivermos em uma rota excluída
    if (isMonitoringPaused.value) return;

    clearTimers(); // Garante que não haja temporizadores ativos antes de criar novos

    inactivityTimer.value = setTimeout(() => {
      showWarning();
    }, INACTIVITY_TIMEOUT);
  };

  // Limpar todos os temporizadores de forma segura
  const clearTimers = () => {
    if (inactivityTimer.value !== null) {
      clearTimeout(inactivityTimer.value);
      inactivityTimer.value = null;
    }

    if (warningTimer.value !== null) {
      clearTimeout(warningTimer.value);
      warningTimer.value = null;
    }

    if (countdownInterval.value !== null) {
      clearInterval(countdownInterval.value);
      countdownInterval.value = null;
    }
  };

  // Mostrar o aviso de inatividade
  const showWarning = () => {
    // Não mostrar aviso se estivermos em uma rota excluída
    if (isMonitoringPaused.value) return;

    // Garantir que os temporizadores anteriores estejam limpos
    clearTimers();

    isWarningVisible.value = true;
    remainingSeconds.value = 30;

    // Iniciar contagem regressiva usando requestAnimationFrame para maior precisão
    let lastTime = Date.now();

    countdownInterval.value = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastTime;

      // Só decrementar se passou aproximadamente 1 segundo (com pequena tolerância)
      if (elapsed >= 1000) {
        remainingSeconds.value -= Math.min(Math.round(elapsed / 1000), 1); // Limita a 1 segundo por vez
        lastTime = now;

        if (remainingSeconds.value <= 0) {
          performLogout();
        }
      }
    }, 100); // Intervalo mais curto para melhor precisão

    // Definir temporizador para o logout automático
    warningTimer.value = setTimeout(() => {
      performLogout();
    }, WARNING_DURATION);
  };

  // Esconder o aviso de inatividade
  const hideWarning = () => {
    isWarningVisible.value = false;
    clearTimers();
    startTimers();
  };

  // Realizar o logout por inatividade
  const performLogout = async () => {
    // Evitar múltiplos logouts simultâneos
    if (isLoggingOut.value) return;

    try {
      isLoggingOut.value = true;

      // Limpar todos os temporizadores antes de fazer logout
      clearTimers();
      isWarningVisible.value = false;

      // Fazer logout local
      await authStore.logout();

      // Navegar para a página de login
      router.push('/login?reason=inactivity');
    } catch (error) {
      console.error('Erro durante o logout por inatividade:', error);

      // Mesmo com erro, garantir o logout local
      await authStore.logout();
      router.push('/login?reason=inactivity');
    } finally {
      // Resetar flag de logout após um tempo para garantir conclusão das operações
      setTimeout(() => {
        isLoggingOut.value = false;
      }, 1000);
    }
  };

  // Continuar sessão (resposta ao aviso)
  const continueSession = async () => {
    try {
      // Esconder o aviso e limpar os temporizadores para evitar logout durante a chamada API
      isWarningVisible.value = false;
      clearTimers();

      // Usar o método do store para estender a sessão
      await authStore.extendSession();
    } catch (error) {
      console.error('Erro ao continuar sessão:', error);
    } finally {
      // Sempre reiniciar os temporizadores após processar a ação
      startTimers();
    }
  };

  // Configurar os ouvintes de eventos
  const setupEventListeners = () => {
    // Primeiro remover quaisquer listeners existentes para evitar duplicação
    cleanupEventListeners();

    // Adicionar novos listeners
    events.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });
  };

  // Remover os ouvintes de eventos
  const cleanupEventListeners = () => {
    events.forEach(event => {
      window.removeEventListener(event, resetInactivityTimer);
    });
  };

  // Iniciar o monitoramento de inatividade
  const startMonitoring = () => {
    // Verificar se o usuário está autenticado antes de iniciar
    if (!authStore.isAuthenticated) return;

    // Verificar se estamos em uma rota excluída
    isMonitoringPaused.value = shouldExcludeRoute();

    // Se não estiver pausado, iniciar monitoramento normal
    if (!isMonitoringPaused.value) {
      setupEventListeners();
      startTimers();
    }
  };

  // Pausar o monitoramento
  const pauseMonitoring = () => {
    isMonitoringPaused.value = true;
    clearTimers();
    isWarningVisible.value = false;
  };

  // Retomar o monitoramento
  const resumeMonitoring = () => {
    isMonitoringPaused.value = false;
    startTimers();
  };

  // Parar o monitoramento de inatividade
  const stopMonitoring = () => {
    cleanupEventListeners();
    clearTimers();
    isWarningVisible.value = false;
    isLoggingOut.value = false;
  };

  // Monitorar mudanças na rota para pausar/retomar o monitoramento conforme necessário
  watch(() => route.path, (newPath) => {
    const shouldExclude = paginasComInatividadePermitida.some(path => newPath === path || newPath.startsWith(path + '/'));
    
    if (shouldExclude && !isMonitoringPaused.value) {
      pauseMonitoring();
    } else if (!shouldExclude && isMonitoringPaused.value) {
      resumeMonitoring();
    }
  });

  // Configurar e limpar quando o componente for montado/desmontado
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startMonitoring();
    }
  });

  onUnmounted(() => {
    stopMonitoring();
  });

  return {
    isWarningVisible,
    remainingSeconds,
    continueSession,
    performLogout,
    startMonitoring,
    stopMonitoring,
    pauseMonitoring,
    resumeMonitoring,
    isMonitoringPaused
  };
}
